import React from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Group,
  rem,
  Box
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconRefresh,
  IconHome
} from '@tabler/icons-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // 更新 state 使下一次渲染顯示錯誤 UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // 記錄錯誤詳細信息
    const errorId = Date.now().toString(36);
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // 將錯誤信息發送到錯誤報告服務（可選）
    console.error('Error Boundary 捕獲到錯誤:', {
      errorId,
      error: error.toString(),
      errorInfo,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });

    // 可以在這裡添加錯誤上報邏輯
    // 例如：發送到 Sentry、LogRocket 等錯誤監控服務
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleResetError = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      return (
        <Container 
          size="sm" 
          style={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            padding: rem(20)
          }}
        >
          <Paper
            shadow="xl"
            radius="lg"
            p="xl"
            style={{
              width: '100%',
              maxWidth: rem(600),
              backgroundColor: '#fff8f8',
              border: '1px solid #ffebee'
            }}
          >
            <Stack gap="lg" align="center">
              <Group gap="md">
                <IconAlertTriangle size={48} color="#d32f2f" />
                <Title
                  order={1}
                  size="xl"
                  style={{
                    color: '#d32f2f',
                    textAlign: 'center'
                  }}
                >
                  應用發生錯誤
                </Title>
              </Group>

              <Text
                size="lg"
                style={{
                  textAlign: 'center',
                  color: '#666',
                  lineHeight: 1.6
                }}
              >
                抱歉，應用遇到了意外錯誤。
                <br />
                請嘗試重新載入頁面或返回首頁。
              </Text>

              {this.state.errorId && (
                <Box
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: rem(12),
                    borderRadius: rem(6),
                    fontFamily: 'monospace',
                    fontSize: rem(12)
                  }}
                >
                  錯誤 ID: {this.state.errorId}
                </Box>
              )}

              <Group gap="md" mt="md">
                <Button
                  variant="filled"
                  color="blue"
                  size="lg"
                  leftSection={<IconRefresh size={20} />}
                  onClick={this.handleReload}
                >
                  重新載入
                </Button>
                
                <Button
                  variant="outline"
                  color="gray"
                  size="lg"
                  leftSection={<IconHome size={20} />}
                  onClick={this.handleGoHome}
                >
                  返回首頁
                </Button>

                {isDevelopment && (
                  <Button
                    variant="outline"
                    color="orange"
                    size="lg"
                    onClick={this.handleResetError}
                  >
                    重置錯誤狀態
                  </Button>
                )}
              </Group>

              {/* 開發環境下顯示詳細錯誤信息 */}
              {isDevelopment && this.state.error && (
                <Box
                  style={{
                    width: '100%',
                    marginTop: rem(20),
                    padding: rem(16),
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: rem(6),
                    fontSize: rem(12),
                    fontFamily: 'monospace'
                  }}
                >
                  <Text fw={600} mb="sm" c="orange.8">
                    開發模式 - 詳細錯誤信息：
                  </Text>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>
                    {this.state.error.toString()}
                  </Text>
                  {this.state.errorInfo && (
                    <>
                      <Text fw={600} mt="sm" mb="xs" c="orange.8">
                        組件堆疊：
                      </Text>
                      <Text style={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.errorInfo.componentStack}
                      </Text>
                    </>
                  )}
                </Box>
              )}
            </Stack>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
